<template>
  <div v-show="open" data-test="chat-panel">
    <div class="fixed inset-0 z-30 bg-gray-500/40" data-test="chat-backdrop" @click="$emit('close')"></div>

    <div class="fixed inset-y-0 right-0 z-40 flex w-full max-w-2xl flex-col bg-white shadow-xl">
      <div class="flex items-center justify-between border-b border-gray-200 p-4">
        <h2 class="text-lg font-semibold text-gray-900">{{ t("title") }}</h2>
        <button type="button" data-test="chat-close" :aria-label="t('close_button')" @click="$emit('close')">
          <XIcon class="h-5 w-5 text-gray-500 hover:text-gray-700" aria-hidden="true" />
        </button>
      </div>

      <div v-if="loading" class="flex flex-1 items-center justify-center">
        <Spinner data-test="chat-loading" />
      </div>

      <template v-else>
        <div ref="thread" class="flex-1 space-y-3 overflow-y-auto p-4">
          <template v-if="messages.length === 0 && !sending">
            <p data-test="chat-empty-state" class="text-sm text-gray-500">
              {{ t("empty_state") }}
            </p>
            <button
              v-if="claimStatus === 'to_be_approved'"
              type="button"
              data-test="chat-suggestion"
              class="rounded-full border border-gray-300 px-3 py-1.5 text-left text-sm text-gray-600 hover:bg-gray-50"
              @click="applySuggestion"
            >
              {{ t("suggested_question") }}
            </button>
          </template>

          <div
            v-for="(message, index) in messages"
            :key="message.id || `local-${index}`"
            class="flex flex-col"
            :class="message.role === 'user' ? 'items-end' : 'items-start'"
          >
            <div
              data-test="chat-message"
              :data-role="message.role"
              class="rounded-xl px-4 py-2 text-sm"
              :class="[
                message.role === 'user'
                  ? 'max-w-xs bg-primary-light text-gray-900 whitespace-pre-line'
                  : 'w-full bg-gray-100 text-gray-900',
              ]"
            >
              <MarkdownText v-if="message.role !== 'user'" :content="message.content" />
              <template v-else>{{ message.content }}</template>
            </div>

            <template v-if="message.toolNames && message.toolNames.length > 0">
              <button
                type="button"
                data-test="chat-tools-toggle"
                class="mt-1 text-xs text-gray-400 hover:text-gray-600"
                @click="toggleTools(message.id)"
              >
                {{ t("tools_used", message.toolNames.length) }} {{ isToolsExpanded(message.id) ? "▾" : "▸" }}
              </button>
              <ul
                v-if="isToolsExpanded(message.id)"
                data-test="chat-tools-list"
                class="mt-1 space-y-0.5 text-xs text-gray-500"
              >
                <li v-for="name in message.toolNames" :key="name">{{ name }}</li>
              </ul>
            </template>
          </div>

          <div v-if="sending" class="flex justify-start">
            <div data-test="chat-pending" class="bg-gray-100 rounded-xl px-4 py-2">
              <Spinner />
            </div>
          </div>

          <p v-if="error" data-test="chat-error" class="text-sm text-red-600">
            {{ t("reply_error") }}
          </p>
        </div>

        <div class="flex items-end gap-2 border-t border-gray-200 p-4">
          <textarea
            v-model="draft"
            data-test="chat-input"
            rows="2"
            :placeholder="t('input_placeholder')"
            :disabled="sending"
            class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md resize-none"
            :class="sending ? 'bg-gray-200' : ''"
            @keydown.enter.exact.prevent="send"
          ></textarea>
          <PrimaryButton data-test="chat-send" :disabled="!canSend" :loading="sending" @click="send">
            {{ t("send_button") }}
          </PrimaryButton>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import { Spinner, PrimaryButton, MarkdownText, toCamelCase } from "@estaly/ui";
import { XIcon } from "@heroicons/vue/outline";
import ClaimChatService from "@/api/services/claimChat";

// The reply is generated by a background job (the estaly-ai call exceeds
// Heroku's 30s request limit), so the thread is polled until it lands.
const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 120000;

export default {
  name: "ClaimChatPanel",

  components: { Spinner, PrimaryButton, MarkdownText, XIcon },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  props: {
    declarationId: {
      type: [Number, String],
      required: true,
    },
    claimStatus: {
      type: String,
      default: null,
    },
    open: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["close"],

  data() {
    return {
      messages: [],
      draft: "",
      loading: false,
      sending: false,
      error: false,
      expandedToolMessageIds: [],
      pollTimer: null,
    };
  },

  computed: {
    canSend() {
      return this.draft.trim().length > 0 && !this.sending;
    },
  },

  watch: {
    async declarationId() {
      // The component stays mounted when navigating between claims: the polling
      // loop, draft and expanded tool lines of the previous thread must not survive.
      this.stopPolling();
      this.draft = "";
      this.error = false;
      this.expandedToolMessageIds = [];
      await this.fetchMessages();
      this.resumePollingIfPending();
    },
    open(value) {
      if (value) this.scrollToBottom();
    },
  },

  async mounted() {
    await this.fetchMessages();
    this.resumePollingIfPending();
  },

  beforeUnmount() {
    this.stopPolling();
  },

  methods: {
    // silent: refresh without the full-panel spinner — keeps the thread
    // mounted so the scroll position is not reset while reloading.
    async fetchMessages({ silent = false } = {}) {
      if (!silent) this.loading = true;
      const requestedDeclarationId = this.declarationId;
      try {
        const response = await ClaimChatService.index(requestedDeclarationId);
        // Drop a response for a claim we have since navigated away from (a stale
        // in-flight poll must not clobber the new thread), and never wipe the
        // thread on a transient failure — that would stop the polling loop
        // silently and drop the message the user just sent.
        if (requestedDeclarationId !== this.declarationId || response.success === false) return;
        this.messages = toCamelCase(response.data) || [];
        this.scrollToBottom();
      } finally {
        if (!silent) this.loading = false;
      }
    },

    async send() {
      if (!this.canSend) return;

      const content = this.draft.trim();
      // The user message is persisted server-side, so it stays displayed
      // regardless of the outcome.
      this.messages.push({ role: "user", content });
      this.draft = "";
      this.error = false;
      this.scrollToBottom();

      const response = await ClaimChatService.create(this.declarationId, content);
      if (response.success === false) {
        this.error = true;
        return;
      }
      // The reply is generated asynchronously: poll the thread until it lands.
      this.startPolling();
    },

    // The thread is "awaiting a reply" while its last message is from the user;
    // the assistant reply becoming the last message ends the wait.
    lastMessageIsFromUser() {
      const last = this.messages[this.messages.length - 1];
      return Boolean(last) && last.role === "user";
    },

    resumePollingIfPending() {
      if (this.lastMessageIsFromUser()) this.startPolling();
    },

    startPolling() {
      if (this.pollTimer) return;
      this.sending = true;
      this.error = false;
      let attempts = 0;
      const maxAttempts = Math.ceil(POLL_TIMEOUT_MS / POLL_INTERVAL_MS);
      this.pollTimer = setInterval(async () => {
        attempts += 1;
        await this.fetchMessages({ silent: true });
        if (!this.lastMessageIsFromUser()) {
          this.stopPolling();
        } else if (attempts >= maxAttempts) {
          this.stopPolling();
          this.error = true;
        }
      }, POLL_INTERVAL_MS);
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
      this.sending = false;
    },

    applySuggestion() {
      this.draft = this.t("suggested_question");
    },

    toggleTools(messageId) {
      if (this.expandedToolMessageIds.includes(messageId)) {
        this.expandedToolMessageIds = this.expandedToolMessageIds.filter((id) => id !== messageId);
      } else {
        this.expandedToolMessageIds.push(messageId);
      }
    },

    isToolsExpanded(messageId) {
      return this.expandedToolMessageIds.includes(messageId);
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const thread = this.$refs.thread;
        if (thread) thread.scrollTop = thread.scrollHeight;
      });
    },
  },
};
</script>

<i18n src="./ClaimChatPanel.i18n.json"></i18n>
